import { useDispatch, useSelector } from 'react-redux';

import { request } from '@api/request';
import getActionCreators from '@redux/getActionCreators';
import { useInjectResourceControllerReducer } from '@redux/getReducer';
import getSelectors from '@redux/getSelectors';
import { createStructuredSelector } from 'reselect';

import useCatchHttpError from './useCatchHttpError';

type TUseRequestParms<T> = {
  reduxActionName: string;
  controllerFunc: T;
  isAutoCatchingError?: boolean;
};

type TUseRequestReturn<T extends (...args: any[]) => any> = {
  resData: any;
  requestLoading: any;
  requestSuccess: any;
  error: any;
  errorData: any;
  errorMessage: any;
  paramsForRequest: any;
  setParamsForRequest: any;
  request: (data?: Parameters<T>[0]) => unknown;
  requestForSingle: any;
  clearData: any;
};

export function useRequest<T extends (...args: any[]) => any>({
  reduxActionName,
  controllerFunc,
  isAutoCatchingError = true,
}: TUseRequestParms<T>): TUseRequestReturn<T> {
  const {
    makeSelectParamsForRequest,
    makeSelectData,
    makeSelectRequestLoading,
    makeSelectRequestSuccess,
    makeSelectRequestError,
    makeSelectRequestErrorMessage,
    makeSelectRequestErrorData,
  } = getSelectors(reduxActionName);
  useInjectResourceControllerReducer(reduxActionName);

  const { resData, requestLoading, requestSuccess, error, errorData, errorMessage, paramsForRequest } = useSelector(
    createStructuredSelector({
      resData: makeSelectData(),
      requestLoading: makeSelectRequestLoading(),
      requestSuccess: makeSelectRequestSuccess(),
      error: makeSelectRequestError(),
      errorData: makeSelectRequestErrorData(),
      errorMessage: makeSelectRequestErrorMessage(),
      paramsForRequest: makeSelectParamsForRequest(),
    }),
  );

  const dispatch = useDispatch();

  useCatchHttpError({ errorMessage, isAutoCatchingError });

  const { setParamsForRequest, clearData } = getActionCreators(reduxActionName);

  const defaultOptionsForRequest = {
    reduxActionName,
    controllerFunc,
    dispatch,
    isSingleReuquest: false,
  };

  return {
    resData,
    requestLoading,
    requestSuccess,
    error,
    errorData,
    errorMessage,
    paramsForRequest,
    setParamsForRequest: (data: any) => dispatch(setParamsForRequest(data)),
    request: (data: any = {}) =>
      request({ ...defaultOptionsForRequest, params: { ...(paramsForRequest as Record<string, T>), ...data } }),
    requestForSingle: (data: any = {}) =>
      request({
        ...defaultOptionsForRequest,
        params: { ...(paramsForRequest as Record<string, T>), ...data },
        isSingleRequest: true,
      }),
    clearData: () => dispatch(clearData()),
  };
}
